import { action, computed, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { HttpAbortedError } from '../../../app/data/HttpAbortedError';
import { TextInput } from '../../../app/shared/Form';
import Logger from '../../../app/shared/Logger';
import { ModalState } from '../../../app/shared/ModalState';
import type { CustomerPricelistItemData, PricingContext } from '../../model';
import { GetProductModelsDetailsQuery } from '../../queries';
import type { PricelistDocumentData } from '../pdfDocuments/PricelistDocument/PricelistDocumentData';
import type { ExportPricelistToPdfStatus } from './ExportPricelistToPdfStatus';

type LoadingStatus = 'pending' | 'error' | 'success';

export interface ExportPricelistToPdfData extends Omit<PricelistDocumentData, 'items' | 'showNetPrices'> {
  productIds: string[];
}

export default class ExportPricelistToPdfModalState extends ModalState {
  @observable public title = '';
  @observable public includeNetPrice = false;
  @observable private loadingStatus: LoadingStatus = 'pending';
  @observable.ref private items: CustomerPricelistItemData[] = [];

  public readonly titleInput = new TextInput();

  /**
   * Aborts the request in flight once the modal is closed or reopened, so the server stops loading
   * products nobody is waiting for and a stale response never replaces the current one.
   */
  private abortController: AbortController | null = null;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    private readonly getPricelistData: () => ExportPricelistToPdfData,
  ) {
    super();
    makeObservable(this);
  }

  public get documentData(): PricelistDocumentData {
    const { createdAt, locale } = this.getPricelistData();
    const showNetPrices = this.canIncludeNetPrice && this.includeNetPrice;
    return { createdAt, items: this.items, locale, showNetPrices, title: this.title };
  }

  public get canIncludeNetPrice(): boolean {
    return this.pricingContext.hasAgreements;
  }

  @action.bound
  public setIncludeNetPrice(includeNetPrice: boolean): void {
    this.includeNetPrice = includeNetPrice;
  }

  @computed
  public get status(): ExportPricelistToPdfStatus {
    if (this.loadingStatus === 'error') {
      return 'error';
    }

    return this.loadingStatus === 'pending' ? 'loadingData' : 'success';
  }

  /** A title other than the one the document already carries, an empty one included. */
  @computed
  public get canChangeTitle(): boolean {
    return this.titleInput.value.trim() !== this.title;
  }

  @action.bound
  public changeTitle(): void {
    if (this.canChangeTitle) {
      this.title = this.titleInput.value.trim();
    }
  }

  protected onAfterOpen(): void {
    const { productIds, title = '' } = this.getPricelistData();

    this.titleInput.setDefaultValue(title);
    this.title = title.trim();
    this.includeNetPrice = false;

    // TODO: Consider if products should be fetched every time or only if product set changed
    this.loadItems(productIds);
  }

  protected onAfterClose(): void {
    this.abortRequest();
    super.onAfterClose();
  }

  private async loadItems(productIds: string[]): Promise<void> {
    this.abortRequest();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setLoadingStatus('pending');

    try {
      const response = await this.client.send(
        new GetProductModelsDetailsQuery({ pricingContext: this.pricingContext, productIds }),
        { signal },
      );

      const items: CustomerPricelistItemData[] = response.items.map(
        ({ productDetails, familyDetails, accessories }) => ({
          id: productDetails.id,
          productModelId: productDetails.id,
          product: productDetails,
          familyDetails,
          accessories,
        }),
      );

      this.setItems(items);
      this.setLoadingStatus('success');
    } catch (error) {
      // Aborted because the modal was closed or reopened: nobody is waiting for these products any more.
      if (error instanceof HttpAbortedError) {
        return;
      }

      Logger.exception('Loading the products to export failed', error);
      this.setLoadingStatus('error');
    }
  }

  private abortRequest(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  @action
  private setItems(items: CustomerPricelistItemData[]): void {
    this.items = items;
  }

  @action
  private setLoadingStatus(loadingStatus: LoadingStatus): void {
    this.loadingStatus = loadingStatus;
  }
}
